import React, { useState, useEffect } from 'react';

import API from '../services/api';

const ACTION_MAP = {
  CREATE: { label: 'සෑදීය',       css: 'bg-green-100 text-green-700',  icon: '➕' },
  EDIT:   { label: 'සංස්කරණය',   css: 'bg-blue-100 text-blue-700',    icon: '✏️' },
  DELETE: { label: 'මකා දැමීය',  css: 'bg-red-100 text-red-700',      icon: '🗑️' },
};

export default function ActivityLogPage() {
  const [logs,      setLogs]      = useState([]);
  const [users,     setUsers]     = useState([]);
  const [stats,     setStats]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [clearing,  setClearing]  = useState(false);

  // ── Filters ──
  const [filterUser,   setFilterUser]   = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom,   setFilterFrom]   = useState('');
  const [filterTo,     setFilterTo]     = useState('');

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterUser)   params.append('user_id', filterUser);
      if (filterAction) params.append('action',  filterAction);
      if (filterFrom)   params.append('date_from', filterFrom);
      if (filterTo)     params.append('date_to',   filterTo);

      const [logsRes, usersRes, statsRes] = await Promise.all([
        API.get(`/activity-logs?${params}`),
        API.get('/activity-logs/users'),
        API.get('/activity-logs/stats'),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { loadLogs(); }, []);

  const handleFilter = (e) => {
    e.preventDefault();
    loadLogs();
  };

  const handleClear = async () => {
    if (!window.confirm('සියලු activity logs මකා දැමීමට අවශ්‍යද?')) return;
    setClearing(true);
    try {
      await API.delete('/activity-logs/clear');
      loadLogs();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setClearing(false);
    }
  };

  const handleReset = () => {
    setFilterUser('');
    setFilterAction('');
    setFilterFrom('');
    setFilterTo('');
    setTimeout(() => loadLogs(), 100);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-gray-500 text-sm mt-1">පරිශීලක ක්‍රියාකාරකම් නිරීක්ෂණය</p>
        </div>
        <button
          onClick={handleClear}
          disabled={clearing}
          className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50">
          🗑️ {clearing ? 'මකමින්...' : 'Logs මකන්න'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { action: 'CREATE', label: 'සෑදූ වාර්තා',     bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
          { action: 'EDIT',   label: 'සංස්කරණය කළ',    bg: 'bg-blue-50',  text: 'text-blue-700',  border: 'border-blue-200'  },
          { action: 'DELETE', label: 'මකා දැමූ',        bg: 'bg-red-50',   text: 'text-red-700',   border: 'border-red-200'   },
        ].map(({ action, label, bg, text, border }) => {
          const stat  = stats.find(s => s.action === action);
          const count = stat ? stat.count : 0;
          return (
            <div key={action} className={`${bg} ${border} border rounded-xl p-4`}>
              <p className={`text-3xl font-bold ${text}`}>{count}</p>
              <p className={`text-sm mt-1 font-medium ${text}`}>{label}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <form onSubmit={handleFilter} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* User filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">පරිශීලකයා</label>
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-red-500 focus:outline-none">
              <option value="">සියලු දෙනා</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.user_name}</option>
              ))}
            </select>
          </div>

          {/* Action filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">ක්‍රියාව</label>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-red-500 focus:outline-none">
              <option value="">සියලු ක්‍රියා</option>
              <option value="CREATE">➕ සෑදීය</option>
              <option value="EDIT">✏️ සංස්කරණය</option>
              <option value="DELETE">🗑️ මකා දැමීය</option>
            </select>
          </div>

          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">දිනය (සිට)</label>
            <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-red-500 focus:outline-none" />
          </div>

          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">දිනය (දක්වා)</label>
            <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-red-500 focus:outline-none" />
          </div>

          {/* Buttons */}
          <div className="col-span-2 md:col-span-4 flex gap-3">
            <button type="submit"
              className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition">
              සොයන්න
            </button>
            <button type="button" onClick={handleReset}
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 px-5 py-2 rounded-lg text-sm font-medium transition">
              නැවත සකස් කරන්න
            </button>
          </div>
        </form>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-gray-400">පූරණය වෙමින්...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-sm">Activity logs නොමැත</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'දිනය/වේලාව', 'පරිශීලකයා', 'භූමිකාව', 'ක්‍රියාව', 'විස්තරය', 'පාසල'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log, i) => {
                  const a = ACTION_MAP[log.action] || { label: log.action, css: 'bg-gray-100 text-gray-600', icon: '•' };
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                        {new Date(log.created_at).toLocaleString('si-LK', {
                          year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{log.user_name}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{log.role_name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${a.css}`}>
                          {a.icon} {a.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs">{log.description}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{log.school_name || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
              මුළු logs: {logs.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}